<template>
  <div class="base-layout" :class="{ 'base-layout--top-bar-hidden': isTopBarHidden }">
    <Sidebar v-model="isSidebarCollapsed" ref="sidebarRef" />
    <div class="base-layout__page">
      <div ref="content" class="base-layout__content">
        <NavigationBar @handleToggle="handleToggle" @focusin.native="showTopBar" />

        <keep-alive :include="['PageTenders', 'PageExplore', 'PageOpportunities', 'PageBusinessIntelligence']">
          <router-view />
        </keep-alive>
      </div>
    </div>
    <DocumentsDownloadedDialog ref="documentsDownloadedDialog" />
    <ChromeExtensionStuckDialog ref="chromeExtensionStuckDialog" />
    <MissingRequiredSeaoRoleDialog ref="missingRequiredSeaoRoleDialog" />
  </div>
</template>

<script>
import Sidebar from '@/components/layout/Sidebar';
import NavigationBar from '@/components/layout/NavigationBar';
import DocumentsDownloadedDialog from '@/components/chrome-extension/DocumentsDownloadedDialog';
import ChromeExtensionStuckDialog from '@/components/chrome-extension/ChromeExtensionStuckDialog';
import MissingRequiredSeaoRoleDialog from '@/components/chrome-extension/MissingRequiredSeaoRoleDialog';
import { mapActions } from 'vuex';
import ChromeExtensionListenersMixin from '@/mixins/chrome-extension-listeners-mixin';
import { tenantHasFormFillingAccess, tenantHasLLMExtractionsAccess } from '@/util/module-access-util';
import { atRest, followScroll, scrollPosition } from '@/util/top-bar-scroll';
import { viewport } from '@/util/viewport';

const TOP_BAR_HEIGHT = 56; // $menu-top-bar-height

export default {
  name: 'BaseLayout',
  components: {
    NavigationBar,
    Sidebar,
    DocumentsDownloadedDialog,
    ChromeExtensionStuckDialog,
    MissingRequiredSeaoRoleDialog,
  },
  mixins: [ChromeExtensionListenersMixin],
  data() {
    // Below COMPACT_LAYOUT_BELOW an open sidebar covers the page, so it starts closed there: nothing
    // stored (a first visit) or a wider screen's choice would open the menu over the page on load.
    const isSidebarCollapsed = viewport.isCompact || localStorage.getItem('isSidebarCollapsed') === 'true';

    return { isSidebarCollapsed, isTopBarHidden: false };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
  },
  watch: {
    isSidebarCollapsed(value) {
      localStorage.setItem('isSidebarCollapsed', value);
    },
    // A new page, or a screen that turned, starts with the bar.
    $route() {
      this.showTopBar();
    },
    isPhone() {
      this.showTopBar();
    },
  },
  methods: {
    ...mapActions('auth', ['validate']),
    ...mapActions('userPreferences', ['getUserPreferences']),
    ...mapActions('formFill', ['watchActiveFillJobs']),
    ...mapActions('extractionJob', ['watchActiveExtractionJobs']),
    handleToggle() {
      this.$refs.sidebarRef.handleToggle();
    },
    onContentScroll(event) {
      // A menu stays with the bar it opened from.
      if (!this.isPhone || document.querySelector('.menuable__content__active')) return;
      const position = scrollPosition(this.$refs.content);
      this.topBarScroll = followScroll(this.topBarScroll, position, event.timeStamp, TOP_BAR_HEIGHT);
      this.isTopBarHidden = this.topBarScroll.hidden;
    },
    showTopBar() {
      this.topBarScroll = atRest(scrollPosition(this.$refs.content));
      this.isTopBarHidden = false;
    },
  },
  created() {
    this.validate();
    this.getUserPreferences();
    // Re-attach to any running form-fill and extraction jobs so the header's
    // task pills survive a reload on any page (the forms page only re-attaches
    // its own opportunity's job via formFill/watchFillJob, and opportunity
    // pages theirs via extractionJob/watchExtractionJob). Skipped entirely —
    // no job queries — for modules the tenant doesn't have.
    if (tenantHasFormFillingAccess()) this.watchActiveFillJobs().catch(() => {});
    if (tenantHasLLMExtractionsAccess()) this.watchActiveExtractionJobs().catch(() => {});
  },
  mounted() {
    // Not reactive: it changes on every scroll event.
    this.topBarScroll = atRest(0);
    this.$refs.content.addEventListener('scroll', this.onContentScroll, { passive: true });
  },
  beforeDestroy() {
    this.$refs.content.removeEventListener('scroll', this.onContentScroll);
  },
};
</script>

<style lang="scss">
.base-layout {
  display: flex;
  height: var(--viewport-height);
  width: 100%;

  &__page {
    height: var(--viewport-height);
    display: flex;
    flex: auto;
    flex-direction: column;
    overflow: auto;
    background-color: $color-neutral-pale;
  }

  &__content {
    min-height: 100%;
    overflow-y: auto;
  }
}
</style>
